// Thin client for the Hush REST API. The bearer token lives in localStorage.
import type {
  PushViewDto,
  AddSenderResponse,
  CardDto,
  GroupDto,
  IncomeLineDto,
  CategoryDto,
  FeaturesDto,
  PartnerDto,
  PartnerEmailInviteDto,
  PartnerInviteResponse,
  ItemDto,
  LearnedDto,
  SettleViewDto,
  FlowCompareDto,
  FlowExampleDto,
  Mode,
  OverviewDto,
  PasteFiledResponse,
  PastePreviewResponse,
  SignupResponse,
  TransactionDto,
  UserDto,
} from '../../shared/api';
import { ui } from './state';

/**
 * Plan edits carry the month being viewed (null: the active one). A month that kept its own plan
 * (src/services/month-plans.ts) takes the planned amount into that plan only; any other month, live.
 */
const planScoped = (path: string) => (ui.month ? `${path}?month=${encodeURIComponent(ui.month)}` : path);

export interface IncomeFields {
  name: string;
  amountCents: number;
  schedule: 'monthly' | 'twice_monthly' | 'biweekly' | 'weekly';
  day1: number | null;
  day2: number | null;
  anchor: string | null;
  /** "Starts in" (YYYY-MM). */
  startsMonth: string;
}

/** An income line's own fields, from its DTO (for archive and restore). */
export const incomeFields = (l: IncomeLineDto, fallbackMonth: string): IncomeFields => ({
  name: l.name,
  amountCents: l.amountCents,
  schedule: l.schedule,
  day1: l.day1,
  day2: l.day2,
  anchor: l.anchor,
  startsMonth: l.startsMonth ?? fallbackMonth,
});

export interface ItemFields {
  name: string;
  amountCents: number;
  expectedMode: 'fixed' | 'last_month';
  dueDay: number | null;
  cadence: 'monthly' | 'every_n_months' | 'yearly';
  months: number[];
  note: string | null;
  autoLog: boolean;
  /** Yearly items only. */
  spread?: boolean;
  lastPaidOn?: string | null;
  renews?: boolean;
  endsMonth?: string | null;
}

// The session lives in an HttpOnly cookie the browser sends by itself; this code never sees a
// token. Older versions kept the token in localStorage: adoptStoredToken() hands it to the
// server once (it becomes the cookie) and deletes it.
const LEGACY_KEYS = ['hush-token', 'hush-token-own'];

let onUnauthorized: () => void = () => {};
export const handleUnauthorized = (fn: () => void) => (onUnauthorized = fn);

/** Move a token stored by an older version into the session cookie, then forget it. */
export async function adoptStoredToken(): Promise<void> {
  let token: string | null = null;
  try {
    token = localStorage.getItem(LEGACY_KEYS[0]);
    for (const k of LEGACY_KEYS) localStorage.removeItem(k);
  } catch {
    return; /* storage blocked: nothing was stored */
  }
  if (!token) return;
  await fetch('/api/session', { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'X-Hush-Client': 'web' } }).catch(() => {});
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

async function req<T>(method: string, path: string, body?: unknown, extra: Record<string, string> = {}): Promise<T> {
  const headers: Record<string, string> = { 'X-Hush-Client': 'web', ...extra };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  let res: Response;
  try {
    res = await fetch(`/api${path}`, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
  } catch {
    throw new ApiError(0, 'You look offline. Try again in a moment.');
  }
  if (res.status === 401 && path !== '/signup' && !path.startsWith('/session')) onUnauthorized();
  if (!res.ok) {
    const data = (await res.json().catch(() => null)) as ({ error?: string } & Record<string, unknown>) | null;
    const err = new ApiError(res.status, data?.error ?? 'Something went wrong. Try again.');
    if (data) errorData.set(err, data);
    throw err;
  }
  return (res.status === 204 ? null : await res.json()) as T;
}

async function download(path: string, failure: string): Promise<Blob> {
  const res = await fetch(path, { headers: { 'X-Hush-Client': 'web' } });
  if (!res.ok) throw new ApiError(res.status, failure);
  return res.blob();
}

/** Errors may carry extra data, e.g. the amount found in an alert the parser could not read. */
export const errorData = new WeakMap<ApiError, Record<string, unknown>>();

export const api = {
  signup: (name: string, timezone: string, code?: string) =>
    req<SignupResponse>('POST', '/signup', { name, timezone, ...(code ? { code } : {}) }),
  signout: () => req<null>('POST', '/me/signout'),
  overview: (month?: string) => req<OverviewDto>('GET', `/overview${month ? `?month=${month}` : ''}`),
  setMode: (mode: Mode) => req<{ user: UserDto }>('PATCH', '/me', { mode }),

  /** `key`: one per add-sheet submit (Idempotency-Key); a retry with it never logs the purchase twice. */
  addTransaction: (t: { merchant?: string; amountCents: number; tipCents?: number; usuallyTipped?: boolean; categoryId: string | null; date: string; fundedFromCategoryId?: string; note?: string | null }, key?: string) =>
    req<{ transaction: TransactionDto }>('POST', '/transactions', t, key ? { 'Idempotency-Key': key } : {}),
  updateTransaction: (
    id: string,
    patch: Partial<{
      displayName: string;
      everyPurchase: boolean;
      amountCents: number;
      /** The tip on top of the bank charge; null removes it. */
      tipCents: number | null;
      /** "Usually tipped" for this merchant. */
      usuallyTipped: boolean;
      categoryId: string | null;
      date: string;
      itemId: string | null;
      /** "Use for every purchase from <merchant>" (the server's default is yes). */
      rememberForMerchant: boolean;
      /** "Also change the N earlier purchases". */
      applyToEarlier: boolean;
      /** "What was this for?"; null or empty removes it. */
      note: string | null;
    }>,
  ) =>
    req<{ transaction: TransactionDto; alsoChanged: number; splitCancelled?: string; splitResend?: { myPercent: number; note: string | null } }>('PATCH', `/transactions/${encodeURIComponent(id)}`, patch),
  /** "Looks right" and "Looks right for all": mark purchases seen. */
  reviewTransactions: (ids: string[]) => req<{ reviewed: number }>('POST', '/transactions/review', { ids }),
  /** The N in "Also change the N earlier purchases": the merchant's other purchases filed elsewhere. */
  related: (id: string, categoryId: string) =>
    req<{ count: number }>('GET', `/transactions/${encodeURIComponent(id)}/related?categoryId=${encodeURIComponent(categoryId)}`),
  /** "What Hush has learned". */
  learned: () => req<LearnedDto>('GET', '/learned'),
  setRuleCategory: (key: string, categoryId: string) => req<null>('PATCH', `/learned/rules/${encodeURIComponent(key)}`, { categoryId }),
  forgetRule: (key: string) => req<null>('DELETE', `/learned/rules/${encodeURIComponent(key)}`),
  forgetMerge: (key: string) => req<null>('DELETE', `/learned/merges/${encodeURIComponent(key)}`),
  forgetMonthly: (itemId: string, key: string) => req<null>('DELETE', `/learned/monthly/${encodeURIComponent(itemId)}/${encodeURIComponent(key)}`),
  /** "Reset to original": drop the household's name for this merchant and this purchase's rename. */
  resetName: (id: string) => req<{ transaction: TransactionDto }>('POST', `/transactions/${encodeURIComponent(id)}/reset-name`),
  /** null, or what happened to a waiting split request of it (cancelled). */
  deleteTransaction: (id: string) => req<{ splitCancelled?: string } | null>('DELETE', `/transactions/${encodeURIComponent(id)}`),
  /** Splits with the linked partner (src/routes/splits.ts). */
  /** `note` undefined: the purchase's own note goes with it; '' sends none. */
  sendSplit: (transactionId: string, myPercent: number, note?: string) =>
    req<{ transaction: TransactionDto }>('POST', '/splits', { transactionId, myPercent, ...(note !== undefined ? { note } : {}) }),
  /** Chips under "What was this for?": this household's latest notes for the merchant. */
  purchaseNotes: (merchant: string) => req<{ notes: string[] }>('GET', `/transactions/notes?merchant=${encodeURIComponent(merchant)}`),
  /** OK on a "Split update" notice (the cron moved a split, or could not). */
  dismissSplitNotice: (id: string) => req<{ ok: boolean }>('POST', `/splits/notices/${encodeURIComponent(id)}/dismiss`, {}),
  cancelSplit: (id: string) => req<{ transaction: TransactionDto | null }>('POST', `/splits/${encodeURIComponent(id)}/cancel`, {}),
  removeSplit: (id: string) => req<{ transaction: TransactionDto | null }>('POST', `/splits/${encodeURIComponent(id)}/remove`, {}),
  /** `note`: my share's "What was this for?" as the Accept sheet's field holds it ('' is none). */
  acceptSplit: (id: string, categoryId: string, itemId: string | null, note: string) =>
    req<{ transaction: TransactionDto }>('POST', `/splits/${encodeURIComponent(id)}/accept`, { categoryId, ...(itemId ? { itemId } : {}), note }),
  /** The add-a-card flow (src/routes/cardflow.ts). */
  flowExample: (since: string) => req<FlowExampleDto>('GET', `/cardflow/example?since=${encodeURIComponent(since)}`),
  flowCompare: (exampleId: string, gmail: string | null) =>
    req<FlowCompareDto>('GET', `/cardflow/compare?exampleId=${encodeURIComponent(exampleId)}${gmail ? `&gmail=${encodeURIComponent(gmail)}` : ''}`),
  flowAdd: (exampleId: string, gmail: string | null, nickname: string | null) =>
    req<FlowCompareDto & { bankId: string; cardId: string | null }>('POST', '/cardflow/add', { exampleId, gmail, nickname }),
  /** The Splits screen and settle up (src/routes/settle.ts). */
  settle: () => req<{ settle: SettleViewDto }>('GET', '/settle'),
  proposeSettle: (splitIds: string[], netCents: number) => req<{ settle: SettleViewDto }>('POST', '/settle', { splitIds, netCents }),
  disputeSettle: (id: string) => req<{ settle: SettleViewDto }>('POST', `/settle/${encodeURIComponent(id)}/dispute`, {}),
  declineSplit: (id: string) => req<{ declined: true }>('POST', `/splits/${encodeURIComponent(id)}/decline`, {}),
  /** Undo a delete. Brings the original row back, source included. */
  /** Undo of a delete; a waiting split it cancelled comes back too when it can (`splitRestored`), else why not. */
  restoreTransaction: (id: string) =>
    req<{ transaction: TransactionDto; splitRestored?: string; splitNotRestored?: string }>('POST', `/transactions/${encodeURIComponent(id)}/restore`),

  addCategory: (name: string, budgetCents: number, kind: 'spending' | 'set_aside' = 'spending', groupId?: string) =>
    req<{ category: CategoryDto }>('POST', '/categories', { name, budgetCents, kind, ...(groupId ? { groupId } : {}) }),
  /** Up or down one place; across into the next group at its edge. */
  /** Up or down within its group (keyboard), or dropped at an index in a group of the same kind. */
  moveCategory: (id: string, to: 'up' | 'down' | { groupId: string; index: number }) =>
    req<unknown>('POST', `/categories/${encodeURIComponent(id)}/move`, typeof to === 'string' ? { direction: to } : to),
  addGroup: (body: { name: string; kind: 'spending' | 'set_aside'; targetMinPct: number | null; targetMaxPct: number | null }) =>
    req<{ group: GroupDto }>('POST', '/groups', body),
  updateGroup: (id: string, patch: Partial<{ name: string; kind: 'spending' | 'set_aside'; targetMinPct: number | null; targetMaxPct: number | null; collapsed: boolean }>) =>
    req<{ group: GroupDto }>('PATCH', `/groups/${encodeURIComponent(id)}`, patch),
  moveGroup: (id: string, to: 'up' | 'down' | { index: number }) => req<unknown>('POST', `/groups/${encodeURIComponent(id)}/move`, typeof to === 'string' ? { direction: to } : to),
  /** Only an empty group; 409 with the reason otherwise. */
  deleteGroup: (id: string) => req<null>('DELETE', `/groups/${encodeURIComponent(id)}`),
  /** Only an empty category (no purchases, no items); 409 with the reason otherwise. */
  deleteCategory: (id: string) => req<null>('DELETE', `/categories/${encodeURIComponent(id)}`),
  updateCategory: (id: string, patch: Partial<{ name: string; budgetCents: number; extraCents: number; kind: 'spending' | 'set_aside'; groupId: string; itemized: boolean; collapsed: boolean; note: string | null; sameAmount: boolean; billDueDay: number | null; billAutoLog: boolean }>) =>
    req<{ category: CategoryDto }>('PATCH', planScoped(`/categories/${encodeURIComponent(id)}`), patch),
  /** "Plan this category by items": the current amount becomes the extra amount. */
  itemizeCategory: (id: string) => req<{ category: CategoryDto }>('POST', planScoped(`/categories/${encodeURIComponent(id)}/itemize`)),
  /** "Back to one amount": the plain amount becomes this month's total. */
  plainCategory: (id: string) => req<{ category: CategoryDto }>('POST', planScoped(`/categories/${encodeURIComponent(id)}/plain`)),
  addItem: (body: ItemFields & { categoryId: string; kind?: 'expense' | 'transfer'; fromTransactionId?: string }) => req<{ item: ItemDto }>('POST', planScoped('/items'), body),
  /** Done on a transfer item: the planned amount today, or a different amount and day this month. */
  markDone: (id: string, body: { amountCents?: number; date?: string } = {}) =>
    req<{ transaction: TransactionDto; item: ItemDto }>('POST', `/items/${encodeURIComponent(id)}/done`, body),
  /** "Mark not done": removes this month's latest entry (Undo restores it). */
  markNotDone: (id: string, month?: string) =>
    req<{ removedTransactionId: string; removed: { amountCents: number; date: string }; splitCancelled?: string; item: ItemDto }>('POST', `/items/${encodeURIComponent(id)}/not-done${month ? `?month=${month}` : ''}`),
  /** Done on a plain category of money set aside: what is left to move today, or a different amount and day this month. */
  markCategoryDone: (id: string, body: { amountCents?: number; date?: string } = {}) =>
    req<{ transaction: TransactionDto; category: CategoryDto }>('POST', `/categories/${encodeURIComponent(id)}/done`, body),
  /** A yearly item's renewal card: keep (for `on`), cancel, ended, later ("Not yet"), seen (the ended note). */
  renewal: (id: string, body: { answer: 'keep' | 'cancel' | 'ended' | 'later' | 'seen'; on?: string }) =>
    req<{ item: ItemDto }>('POST', `/items/${encodeURIComponent(id)}/renewal`, body),
  /** "Yes, it renewed": the renewal charge, linked to the item (starts the next term). */
  renewed: (id: string, body: { amountCents: number; date: string }) =>
    req<{ transaction: TransactionDto; item: ItemDto }>('POST', `/items/${encodeURIComponent(id)}/renewed`, body),
  /** One tap: log the planned amount (or `itemId`'s amount) to a category, as a purchase. */
  logPlanned: (id: string, body: { date?: string; merchant?: string; itemId?: string } = {}) =>
    req<{ transaction: TransactionDto }>('POST', `/categories/${encodeURIComponent(id)}/log-planned`, body),
  markCategoryNotDone: (id: string, month?: string) =>
    req<{ removedTransactionId: string; removed: { amountCents: number; date: string }; splitCancelled?: string; category: CategoryDto }>('POST', `/categories/${encodeURIComponent(id)}/not-done${month ? `?month=${month}` : ''}`),
  /** "Start your November budget": the next month becomes the active one (POST /api/months/start). */
  startMonth: (month: string) => req<{ activeMonth: string }>('POST', '/months/start', { month }),
  /** A card's nickname; null shows the bank and last four digits again. */
  renameCard: (id: string, nickname: string | null) => req<{ card: CardDto }>('PATCH', `/cards/${encodeURIComponent(id)}`, { nickname }),
  addIncome: (body: IncomeFields) => req<{ line: IncomeLineDto }>('POST', planScoped('/income'), body),
  updateIncome: (id: string, body: IncomeFields & { archived?: boolean }) => req<{ line: IncomeLineDto }>('PATCH', planScoped(`/income/${encodeURIComponent(id)}`), body),
  /** The details text (null clears it). */
  setIncomeDetails: (id: string, details: string | null) => req<{ line: IncomeLineDto }>('PUT', `/income/${encodeURIComponent(id)}/details`, { details }),
  deleteIncome: (id: string) => req<null>('DELETE', planScoped(`/income/${encodeURIComponent(id)}`)),
  /** "Carried over from last month" for a month (typed; can be negative). */
  setCarryOver: (month: string, amountCents: number) => req<unknown>('PUT', '/income/carryover', { month, amountCents }),
  /** "Not now" on the "Add your income" card: hidden for 3 days. */
  dismissIncomeCard: () => req<null>('POST', '/income/card/dismiss'),
  /** "Not now" on the Move your money card: hidden for 3 days. */
  dismissMoveCard: () => req<null>('POST', '/items/move-card/dismiss'),
  updateItem: (id: string, patch: Partial<ItemFields> & { archived?: boolean }) => req<{ item: ItemDto }>('PATCH', planScoped(`/items/${encodeURIComponent(id)}`), patch),
  deleteItem: (id: string) => req<null>('DELETE', planScoped(`/items/${encodeURIComponent(id)}`)),

  dismissInsight: (month: string, key: string) => req<null>('POST', '/insights/dismiss', { month, key }),
  mergeTransaction: (id: string) => req<unknown>('POST', `/transactions/${encodeURIComponent(id)}/merge`),
  keepSeparate: (id: string) => req<unknown>('POST', `/transactions/${encodeURIComponent(id)}/keep-separate`),

  /** An emailed partner invite: who sent it (public, before sign-up). */
  inviteCheck: (token: string) => req<{ inviterName: string; name: string | null; expiresAt: string }>('POST', '/auth/partner-invite/check', { token }),
  /** Sign up from an emailed invite: no code; the email must be the one it was sent to. */
  inviteSignup: (token: string, email: string, name: string, timezone: string) =>
    req<SignupResponse>('POST', '/auth/partner-invite/start', { token, email, name, timezone }),
  /** Signed in already: link with whoever sent this invite. */
  useInvite: (token: string) => req<{ partner: PartnerDto }>('POST', '/partner/invites/use', { token }),
  sendPartnerInvite: (email: string, name?: string) => req<{ invite: PartnerEmailInviteDto }>('POST', '/partner/invites', { email, ...(name ? { name } : {}) }),
  cancelPartnerInvite: (id: string) => req<{ partner: PartnerDto }>('DELETE', `/partner/invites/${encodeURIComponent(id)}`),
  startMagicLink: (email: string) => req<{ status: 'pending'; message: string }>('POST', '/auth/magic-link/start', { email }),
  recover: (code: string) => req<SignupResponse>('POST', '/auth/recovery-code/start', { code }),
  newRecoveryCode: () => req<{ recoveryCode: string }>('POST', '/me/recovery-code'),
  completeMagicLink: (token: string) => req<SignupResponse>('POST', '/auth/magic-link/complete', { token }),
  /** Phone notifications (src/routes/push.ts). */
  pushView: () => req<PushViewDto>('GET', '/push'),
  pushSubscribe: (sub: { endpoint: string; keys: { p256dh: string; auth: string } }) => req<PushViewDto & { deviceId: string }>('POST', '/push/subscribe', sub),
  pushRemove: (id: string) => req<PushViewDto>('DELETE', `/push/devices/${encodeURIComponent(id)}`),
  pushKinds: (kinds: Partial<PushViewDto['kinds']>) => req<PushViewDto>('PATCH', '/push/kinds', kinds),
  pushTest: () => req<PushViewDto & { sent: number }>('POST', '/push/test', {}),
  setSigninEmail: (email: string) => req<{ status: 'pending'; message: string }>('POST', '/me/email', { email }),
  verifyEmail: (token: string) => req<{ email: string }>('POST', '/auth/email/verify', { token }),
  signoutAll: () => req<{ revoked: number }>('POST', '/me/signout-all'),
  partnerInvite: () => req<PartnerInviteResponse>('POST', '/partner/invite', {}),
  partnerAccept: (code: string) => req<{ partner: PartnerDto }>('POST', '/partner/accept', { code }),
  partnerUnlink: () => req<{ partner: PartnerDto }>('POST', '/partner/unlink', {}),
  /** The Gmail filter file, as a Blob for download. */
  gmailFilter: () => download('/api/setup/gmail-filter.xml', 'Could not create the filter file. Try again.'),
  exportCsv: (kind: 'transactions' | 'categories', month: string) =>
    download(`/api/export/${kind}.csv?month=${month}`, 'Could not export. Try again.'),
  config: () => req<{ features: FeaturesDto }>('GET', '/config'),
  /** "Download all my data": one JSON file. */
  exportAll: () => download('/api/me/export', 'Could not download your data. Try again.'),
  /** "Delete my account and all data". `confirm` must be the typed word "delete". */
  deleteAccount: (confirm: string) => req<{ deleted: 'household' | 'user' }>('DELETE', '/me', { confirm }),
  pastePreview: (text: string) => req<PastePreviewResponse>('POST', '/intake/paste', { text }),
  pasteConfirm: (previewId: string) => req<PasteFiledResponse>('POST', `/intake/paste/${encodeURIComponent(previewId)}/confirm`),
  /** The browser switches to the sample household (its own session waits server side). */
  loadSample: () => req<unknown>('POST', '/sample'),
  /** Back to the user's own session after sample data expired. */
  ownSession: () => req<null>('POST', '/session/own'),
  leaveSample: () => req<null>('POST', '/sample/leave'),

  answerPrice: (id: string, answer: 'confirm' | 'reject') =>
    req<unknown>('POST', `/items/${encodeURIComponent(id)}/price/${answer}`),
  answerCategoryPrice: (id: string, answer: 'confirm' | 'reject') => req<{ category: CategoryDto }>('POST', `/categories/${encodeURIComponent(id)}/price/${answer}`),
  resolveReviewItem: (id: string) => req<null>('POST', `/review-items/${encodeURIComponent(id)}/resolve`),
  /** Add an alert sender (answers "New sender: X. Add it?" too). */
  allowSender: (sender: string) => req<AddSenderResponse>('POST', '/alert-senders', { sender }),
  setSenderThreshold: (sender: string, thresholdCents: number | null) =>
    req<null>('POST', '/alert-senders/threshold', { sender, thresholdCents }),
  setBanks: (registryIds: string[]) => req<null>('PUT', '/banks/selection', { registryIds }),
  acceptBankProposal: (id: string, body: { name: string; sender: string; subjectPattern: string; alsoLog: boolean }) =>
    req<{ bankId: string; logged: { status: string; categoryId?: string | null } | null }>('POST', `/banks/proposals/${encodeURIComponent(id)}/accept`, body),
  discardBankProposal: (id: string) => req<null>('POST', `/banks/proposals/${encodeURIComponent(id)}/discard`),
  renameBank: (id: string, name: string) => req<{ name: string }>('PATCH', `/banks/mine/${encodeURIComponent(id)}`, { name }),
  removeBank: (id: string) => req<null>('DELETE', `/banks/mine/${encodeURIComponent(id)}`),
  setShareBanks: (shareBanks: boolean) => req<{ shareBanks: boolean }>('PUT', '/banks/sharing', { shareBanks }),
  dismissSetupCard: () => req<null>('POST', '/setup/card/dismiss'),
  dismissSilence: (key: string) => req<null>('POST', '/banks/silence/dismiss', { key }),
  addMyAddress: (address: string) => req<{ address: string }>('POST', '/setup/my-addresses', { address }),
  removeMyAddress: (address: string) => req<null>('POST', '/setup/my-addresses/remove', { address }),
  deleteHeldMessage: (id: string) => req<null>('DELETE', `/setup/held-messages/${encodeURIComponent(id)}`),
  declineSender: (sender: string) => req<null>('POST', '/alert-senders/decline', { sender }),
  removeSender: (sender: string) => req<null>('POST', '/alert-senders/remove', { sender }),
  /** "Add it": the candidate becomes an item. 409 with needsCategory when its merchant has no category yet. */
  confirmCandidate: (id: string, categoryId?: string) =>
    req<unknown>('POST', `/candidates/${encodeURIComponent(id)}/confirm`, categoryId ? { categoryId } : {}),
  declineCandidate: (id: string) => req<null>('POST', `/candidates/${encodeURIComponent(id)}/decline`),
};
